"use client"

import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import Image from "next/image"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { ArrowUpRight, Trophy, Medal, Award, ThumbsUp, ThumbsDown, Eye } from "lucide-react"
import { formatDistanceToNow } from "date-fns"
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch"
import { VoteButtons, type VoteUpdate } from "./vote-buttons"
import { ShareButtons } from "./share-buttons"
import { ReportPhotoDialog } from "./report-photo-dialog"
import { communityAnalytics } from "@/lib/analytics"

interface LeaderboardPhoto {
  id: string
  slug?: string
  image_url: string
  thumbnail_url?: string
  title: string
  score: number
  upvotes: number
  downvotes: number
  grass_type?: string
  zip_code?: string
  created_at: string
  view_count?: number
  share_count?: number
  social_handle?: string
  rank: number
}

interface LeaderboardTableProps {
  period?: 'all-time' | 'weekly' | 'monthly' | 'regional'
  region?: string
}

const INITIAL_LIMIT = 20
const LOAD_MORE_COUNT = 20

function applyVoteUpdate(
  photos: LeaderboardPhoto[],
  photoId: string,
  update: VoteUpdate,
): LeaderboardPhoto[] {
  return photos
    .map((photo) => photo.id === photoId
      ? {
          ...photo,
          score: update.score,
          upvotes: update.upvotes,
          downvotes: update.downvotes,
        }
      : photo)
    .sort((left, right) => right.score - left.score || left.rank - right.rank)
    .map((photo, index) => ({ ...photo, rank: index + 1 }))
}

export function LeaderboardTable({ period = 'all-time', region }: LeaderboardTableProps) {
  const [photos, setPhotos] = useState<LeaderboardPhoto[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedPhoto, setSelectedPhoto] = useState<LeaderboardPhoto | null>(null)
  const selectedPhotoTriggerRef = useRef<HTMLDivElement | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  const openPhoto = (photo: LeaderboardPhoto, trigger: HTMLDivElement) => {
    selectedPhotoTriggerRef.current = trigger
    setSelectedPhoto(photo)
  }

  useEffect(() => {
    // Reset when filters change
    setPhotos([])
    setHasMore(true)
    fetchLeaderboard(true, { trackView: true })
    // Refetch on filter change; fetchLeaderboard is recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, region])

  const fetchLeaderboard = async (
    isInitial = false,
    options: { preserveLoaded?: boolean; silent?: boolean; trackView?: boolean } = {},
  ) => {
    try {
      if (isInitial && !options.silent) {
        setLoading(true)
      } else if (!isInitial) {
        setLoadingMore(true)
      }
      setError(null)

      const currentOffset = isInitial ? 0 : photos.length
      const limit = isInitial && options.preserveLoaded
        ? Math.min(Math.max(photos.length, INITIAL_LIMIT), 100)
        : isInitial
          ? INITIAL_LIMIT
          : LOAD_MORE_COUNT

      const params = new URLSearchParams({
        period,
        limit: limit.toString(),
        offset: currentOffset.toString(),
        ...(region && { region })
      })

      const response = await fetch(`/api/community/leaderboard?${params}`)
      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard')
      }

      const data = await response.json()
      const newPhotos = data.photos || []

      if (isInitial) {
        setPhotos(newPhotos)
        // Count a leaderboard view per period/region tab the user lands on.
        if (options.trackView) communityAnalytics.leaderboardViewed()
      } else {
        // A concurrent score change can move a row across an offset boundary.
        // Keep the visible list unique even if that happens between requests.
        setPhotos((previous) => {
          const seen = new Set(previous.map((photo) => photo.id))
          return [
            ...previous,
            ...newPhotos.filter((photo: LeaderboardPhoto) => !seen.has(photo.id)),
          ]
        })
      }

      const nextTotalCount = typeof data.totalCount === 'number'
        ? data.totalCount
        : newPhotos.length
      setTotalCount(nextTotalCount)
      setHasMore(currentOffset + newPhotos.length < nextTotalCount)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leaderboard')
      console.error('Error fetching leaderboard:', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchLeaderboard(false)
    }
  }

  const handleVoteUpdated = (photoId: string, update: VoteUpdate) => {
    const nextPhotos = applyVoteUpdate(photos, photoId, update)
    setPhotos(nextPhotos)
    setSelectedPhoto((current) => {
      if (!current || current.id !== photoId) return current

      return nextPhotos.find((photo) => photo.id === photoId) || {
        ...current,
        score: update.score,
        upvotes: update.upvotes,
        downvotes: update.downvotes,
      }
    })

    // Voting can move a row across the next offset boundary. Refresh the
    // complete loaded window from rank one so Load More cannot duplicate or
    // omit a boundary row after the optimistic update.
    void fetchLeaderboard(true, { preserveLoaded: true, silent: true })
  }

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-500" />
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />
      default:
        return <span className="text-2xl font-bold text-gray-600">#{rank}</span>
    }
  }

  const getRankBadgeColor = (rank: number) => {
    switch (rank) {
      case 1:
        return "bg-linear-to-r from-yellow-400 to-yellow-600"
      case 2:
        return "bg-linear-to-r from-gray-400 to-gray-500"
      case 3:
        return "bg-linear-to-r from-amber-500 to-amber-700"
      default:
        return "bg-gray-500"
    }
  }

  if (loading) {
    return (
      <div className="space-y-4" role="status" aria-label="Loading leaderboard results">
        <h2 className="sr-only">Leaderboard results</h2>
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="ds-glass animate-pulse rounded-2xl">
            <div className="p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100/80 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-green-100/80 rounded w-3/4"></div>
                  <div className="h-3 bg-green-100/60 rounded w-1/2"></div>
                </div>
                <div className="h-6 bg-green-100/80 rounded w-16"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-lg font-medium text-gray-900 mb-2">Failed to load leaderboard</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          type="button"
          onClick={() => fetchLeaderboard(true, { trackView: true })}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (photos.length === 0) {
    return (
      <div className="ds-glass rounded-3xl border-dashed py-12 text-center">
        <div className="text-gray-400 mb-4">
          <Trophy className="w-12 h-12 mx-auto" />
        </div>
        <h2 className="text-lg font-medium text-gray-900 mb-2">No ranked lawns yet</h2>
        <p className="text-gray-600 mb-4">
          {region
            ? `No lawns have been ranked in the ${region} region yet.`
            : 'No lawns have been ranked for this time period yet.'}
        </p>
        <Link
          href="/community/submit"
          className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 inline-block"
        >
          Share your lawn
        </Link>
      </div>
    )
  }

  return (
    <>
      <h2 className="sr-only">Leaderboard results</h2>
      <div className="space-y-3 md:space-y-4">
        {photos.map((photo) => {
          const imageUrl = photo.thumbnail_url || photo.image_url
          const timeAgo = formatDistanceToNow(new Date(photo.created_at), { addSuffix: true })

          return (
            <div
            key={photo.id}
            className="ds-glass cursor-pointer rounded-2xl transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-green-500 motion-reduce:transition-none"
            onClick={(event) => openPhoto(photo, event.currentTarget)}
            role="button"
            aria-label={`View ${photo.title}, rank ${photo.rank}, score ${photo.score}`}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openPhoto(photo, e.currentTarget)
              }
            }}
          >
              <div className="p-3 md:p-4">
                <div className="flex items-center gap-3">
                  {/* Rank */}
                  <div className="flex-shrink-0 flex items-center justify-center w-8 md:w-10">
                    {photo.rank <= 3 ? (
                      getRankIcon(photo.rank)
                    ) : (
                      <span className="text-base md:text-lg font-bold text-green-950/40">#{photo.rank}</span>
                    )}
                  </div>

                  {/* Photo Thumbnail */}
                  <div className="flex-shrink-0">
                    <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden bg-gray-100 ring-2 ring-gray-100">
                      <Image
                        src={imageUrl}
                        alt={photo.title}
                        fill
                        className="object-cover"
                        sizes="56px"
                      />
                    </div>
                  </div>

                  {/* Photo Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm md:text-base text-green-950 line-clamp-1">
                      {photo.title}
                    </h3>
                    {/* Stats row */}
                    <div className="flex items-center gap-2 md:gap-3 text-xs text-green-950/55 mt-0.5">
                      <div className="flex items-center gap-1 text-green-600" aria-label={`${photo.upvotes} upvotes`}>
                        <ThumbsUp className="w-3 h-3" aria-hidden="true" />
                        <span>{photo.upvotes}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-400" aria-label={`${photo.downvotes} downvotes`}>
                        <ThumbsDown className="w-3 h-3" aria-hidden="true" />
                        <span>{photo.downvotes}</span>
                      </div>
                      <span className="text-gray-300">•</span>
                      <span className="text-gray-400">{timeAgo}</span>
                    </div>
                  </div>

                  {/* Score badge */}
                  <div className="flex-shrink-0">
                    <div aria-label={`Score ${photo.score}`} className={`inline-flex items-center justify-center min-w-10 px-2.5 py-1 rounded-full text-white font-bold text-sm ${getRankBadgeColor(photo.rank)}`}>
                      {photo.score > 0 ? '+' : ''}{photo.score}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Load More Button */}
      {hasMore && photos.length > 0 && (
        <div className="flex justify-center mt-4">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="ds-glass min-h-11 rounded-full px-6 py-2.5 text-sm font-semibold text-green-950 hover:text-green-700 disabled:opacity-50 transition-colors"
          >
            {loadingMore ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Loading...
              </span>
            ) : (
              `Load More`
            )}
          </button>
        </div>
      )}

      {/* Show count */}
      {photos.length > 0 && (
        <p className="text-center text-xs text-green-950/45 mt-3">
          Showing {photos.length} of {totalCount} {totalCount === 1 ? 'photo' : 'photos'}
        </p>
      )}

      {/* Photo Modal - Clean leaderboard style */}
      {selectedPhoto && (
        <Dialog open={!!selectedPhoto} onOpenChange={(open) => !open && setSelectedPhoto(null)}>
          <DialogContent
            className="sm:max-w-lg max-h-[90vh] p-0 overflow-hidden gap-0"
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              selectedPhotoTriggerRef.current?.focus()
            }}
          >
            {/* Image - edge to edge */}
            <div className="relative bg-black">
              <TransformWrapper
                initialScale={1}
                minScale={0.5}
                maxScale={4}
                centerOnInit={true}
              >
                {() => (
                  <TransformComponent
                    wrapperClass="w-full!"
                    contentClass="w-full!"
                  >
                    <div className="relative w-full aspect-4/3">
                      <Image
                        src={selectedPhoto.image_url}
                        alt={selectedPhoto.title}
                        fill
                        className="object-contain"
                        sizes="(max-width: 768px) 100vw, 512px"
                        priority
                      />
                    </div>
                  </TransformComponent>
                )}
              </TransformWrapper>

              {/* Rank badge overlay */}
              <div className="absolute top-3 left-3">
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white font-bold text-sm shadow-lg ${getRankBadgeColor(selectedPhoto.rank)}`}>
                  {selectedPhoto.rank <= 3 ? (
                    <>
                      {selectedPhoto.rank === 1 && <Trophy className="w-4 h-4" />}
                      {selectedPhoto.rank === 2 && <Medal className="w-4 h-4" />}
                      {selectedPhoto.rank === 3 && <Award className="w-4 h-4" />}
                    </>
                  ) : (
                    <span>#</span>
                  )}
                  <span>{selectedPhoto.rank}</span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-5 py-4 space-y-4">
              <DialogHeader className="p-0 space-y-1">
                <DialogTitle className="text-xl font-semibold text-gray-900">
                  {selectedPhoto.title}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Lawn photo ranked #{selectedPhoto.rank} with {selectedPhoto.upvotes} upvotes
                </DialogDescription>
              </DialogHeader>

              {/* Info row */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4 text-gray-500">
                  <div className="flex items-center gap-1.5 text-green-600 font-medium" aria-label={`${selectedPhoto.upvotes} upvotes`}>
                    <ThumbsUp className="w-4 h-4" aria-hidden="true" />
                    <span>{selectedPhoto.upvotes}</span>
                  </div>
                  <div className="flex items-center gap-1.5" aria-label={`${selectedPhoto.downvotes} downvotes`}>
                    <ThumbsDown className="w-4 h-4" aria-hidden="true" />
                    <span>{selectedPhoto.downvotes}</span>
                  </div>
                  <div className="flex items-center gap-1.5" aria-label={`${selectedPhoto.view_count || 0} views`}>
                    <Eye className="w-4 h-4" aria-hidden="true" />
                    <span>{selectedPhoto.view_count || 0}</span>
                  </div>
                  {selectedPhoto.grass_type && (
                    <span className="text-green-600 font-medium">{selectedPhoto.grass_type}</span>
                  )}
                </div>
                <span className="text-gray-400">
                  {formatDistanceToNow(new Date(selectedPhoto.created_at), { addSuffix: true })}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <VoteButtons
                  photoId={selectedPhoto.id}
                  currentScore={selectedPhoto.score}
                  compact
                  onVoteUpdated={(update) => handleVoteUpdated(selectedPhoto.id, update)}
                />
                <div className="flex items-center gap-0.5">
                <ShareButtons
                    photoSlug={selectedPhoto.slug || selectedPhoto.id}
                    photoId={selectedPhoto.id}
                    title={selectedPhoto.title}
                    compact
                  />
                  <ReportPhotoDialog photoId={selectedPhoto.id} photoTitle={selectedPhoto.title} compact />
                </div>
              </div>

              <Link
                href={`/community/${selectedPhoto.slug || selectedPhoto.id}`}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-green-200 text-sm font-semibold text-green-800 hover:bg-green-50"
              >
                Open lawn details
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
